import React, { useEffect, useState, useRef } from "react";
import Box from "@mui/material/Box";
import { useDispatch, useSelector } from "react-redux";
import ArticlesToolbar from "../../../../components/dashboardcomponents/KnowledgeBase/Articles/ArticlesToolbar";
import ArticlesTable from "../../../../components/dashboardcomponents/KnowledgeBase/Articles/ArticlesTable";
import ArticlesBulkBar from "../../../../components/dashboardcomponents/KnowledgeBase/Articles/ArticlesBulkBar";
import ArticlesSkeleton from "../../../../components/dashboardcomponents/KnowledgeBase/Articles/ArticlesSkeleton";
import { getKbArticles, getKbArticleSearchCounts } from "../../../../store/features/getKbArticles";
import { getKbOverviewStats } from "../../../../store/features/mvKbOverview";
import { deleteKbArticle, archiveKbArticle } from "../../../../store/features/kbActions";
import { updateKbArticleStatus, updateKbArticleVisibility, updateKbArticleCategory } from "../../../../store/features/kbBulkActions";
import "../../TicketManagment/style.css";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from 'react-toastify';
import GroupSelectDropdown from "../../../../components/dashboardcomponents/ProfileComponents/GroupSelectDropdown";
import { useAnalyticsGroupId } from "../../../../hooks/useAnalyticsGroupId";
import useDebounce from "../../../../hooks/useDebounce";
import { fetchWorkspaceSettings } from "../../../../store/features/workspaceSettingsApi";
import { selectBusinessSlug } from "../../../../utils/kbPublicUrl";
import { isHeaderInlinePageTitleEnabled } from "../../../../config/featureFlags";
const TABS = [
  { key: "all", label: "All" },
  { key: "published", label: "Published" },
  { key: "draft", label: "Drafts" },
  { key: "pendingReview", label: "Pending Review" },
  { key: "archived", label: "Archived" },
];

const Articles = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { articles, totalCount, loading, searchCounts } = useSelector((state) => state.kbArticles);
  const { groupStats, groupStatsGroupId } = useSelector((state) => state.kbOverview);

  const business_id = useSelector((state) => state.loginToken?.response?.user?.business_id);
  const kbRole = useSelector((state) => state.loginToken?.response?.user?.kbRole?.role);
  const businessSlug = useSelector(selectBusinessSlug);

  const [tab, setTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [visibility, setVisibility] = useState(""); // All/Internal/Public
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const { selectedGroupId, setSelectedGroupId } = useAnalyticsGroupId();
  const rowsPerPage = 10;
  const debouncedSearch = useDebounce(searchQuery, 400);
  const isLoading = !selectedGroupId || loading;
  const prevGroupIdRef = useRef(selectedGroupId);

  useEffect(() => {
    if (prevGroupIdRef.current === selectedGroupId) return;
    prevGroupIdRef.current = selectedGroupId;
    setSelected([]);
    setPage(0);
  }, [selectedGroupId]);

  useEffect(() => {
    if (business_id && !businessSlug) {
      dispatch(fetchWorkspaceSettings());
    }
  }, [dispatch, business_id, businessSlug]);

  useEffect(() => {
    if (business_id && selectedGroupId) {
      dispatch(getKbOverviewStats({ business_id, group_id: selectedGroupId }));
    }
  }, [dispatch, business_id, selectedGroupId]);

  useEffect(() => {
    if (business_id && selectedGroupId) {
      dispatch(getKbArticles({
        business_id,
        group_id: selectedGroupId,
        page: page + 1,
        limit: rowsPerPage,
        status: tab,
        visibility: visibility,
        search: debouncedSearch
      }));
    }
  }, [dispatch, business_id, selectedGroupId, page, tab, visibility, debouncedSearch, location.key]);

  // Tab counts normally come from /kb/articles/stats, which ignores `search`.
  useEffect(() => {
    if (business_id && selectedGroupId && debouncedSearch) {
      dispatch(getKbArticleSearchCounts({
        business_id,
        group_id: selectedGroupId,
        visibility,
        search: debouncedSearch
      }));
    }
  }, [dispatch, business_id, selectedGroupId, visibility, debouncedSearch]);

  const refreshData = () => {
    if (business_id && selectedGroupId) {
      dispatch(getKbArticles({
        business_id,
        group_id: selectedGroupId,
        page: page + 1,
        limit: rowsPerPage,
        status: tab,
        visibility: visibility,
        search: debouncedSearch
      }));
      dispatch(getKbOverviewStats({ business_id, group_id: selectedGroupId }));
    }
  };

  const handleRowAction = async (type, id) => {
    if (!selectedGroupId) return;
    if (type === 'delete') {
      const result = await dispatch(deleteKbArticle({ articleIds: [id], group_id: selectedGroupId }));
      if (deleteKbArticle.fulfilled.match(result)) {
        toast.success(result.payload.message || 'Article deleted successfully');
        refreshData();
      } else {
        toast.error(result.payload || 'Failed to delete article');
      }
    } else if (type === 'archive') {
      const result = await dispatch(archiveKbArticle({ articleId: id, group_id: selectedGroupId }));
      if (archiveKbArticle.fulfilled.match(result)) {
        toast.success(result.payload.message || 'Article archived successfully');
        refreshData();
      } else {
        toast.error(result.payload || 'Failed to archive article');
      }
    } else if (type === 'publish') {
      const result = await dispatch(updateKbArticleStatus({ articleIds: [id], status: 'published', group_id: selectedGroupId }));
      if (updateKbArticleStatus.fulfilled.match(result)) {
        toast.success(result.payload.message || 'Article published successfully');
        refreshData();
      } else {
        toast.error(result.payload || 'Failed to publish article');
      }
    } else if (type === 'unpublish') {
      const result = await dispatch(updateKbArticleStatus({ articleIds: [id], status: 'pendingReview', group_id: selectedGroupId }));
      if (updateKbArticleStatus.fulfilled.match(result)) {
        toast.success(result.payload.message || 'Article unpublished successfully');
        refreshData();
      } else {
        toast.error(result.payload || 'Failed to unpublish article');
      }
    } else if (type === 'public') {
      const result = await dispatch(updateKbArticleVisibility({ articleIds: [id], visibility: 'public', group_id: selectedGroupId }));
      if (updateKbArticleVisibility.fulfilled.match(result)) {
        toast.success(result.payload.message || 'Article visibility updated to public');
        refreshData();
      } else {
        toast.error(result.payload || 'Failed to update visibility');
      }
    } else if (type === 'internal') {
      const result = await dispatch(updateKbArticleVisibility({ articleIds: [id], visibility: 'internal', group_id: selectedGroupId }));
      if (updateKbArticleVisibility.fulfilled.match(result)) {
        toast.success(result.payload.message || 'Article visibility updated to internal');
        refreshData();
      } else {
        toast.error(result.payload || 'Failed to update visibility');
      }
    }
  };

  const handleBulkChange = async (field, value) => {
    if (!selected.length || !value || !selectedGroupId) return;

    if (field === 'status') {
      const result = await dispatch(updateKbArticleStatus({ articleIds: selected, status: value, group_id: selectedGroupId }));
      if (updateKbArticleStatus.fulfilled.match(result)) {
        toast.success(result.payload.message || 'Status updated successfully');
        dispatch(getKbOverviewStats({ business_id, group_id: selectedGroupId }));
        refreshData();
        setSelected([]);
      } else {
        toast.error(result.payload || 'Failed to update status');
      }
    } else if (field === 'visibility') {
      const result = await dispatch(updateKbArticleVisibility({ articleIds: selected, visibility: value, group_id: selectedGroupId }));
      if (updateKbArticleVisibility.fulfilled.match(result)) {
        toast.success(result.payload.message || 'Visibility updated successfully');
        dispatch(getKbOverviewStats({ business_id, group_id: selectedGroupId }));
        refreshData();
        setSelected([]);
      } else {
        toast.error(result.payload || 'Failed to update visibility');
      }
    } else if (field === 'category') {
      const categoryIds = Array.isArray(value) ? value : [value];
      const result = await dispatch(updateKbArticleCategory({ articleIds: selected, categoryIds, group_id: selectedGroupId }));
      if (updateKbArticleCategory.fulfilled.match(result)) {
        toast.success(result.payload.message || 'Category updated successfully');
        dispatch(getKbOverviewStats({ business_id, group_id: selectedGroupId }));
        refreshData();
        setSelected([]);
      } else {
        toast.error(result.payload || 'Failed to update category');
      }
    }
  };

  const handleBulkDelete = async () => {
    if (!selected.length || !selectedGroupId) return;
    const result = await dispatch(deleteKbArticle({ articleIds: selected, group_id: selectedGroupId }));
    if (deleteKbArticle.fulfilled.match(result)) {
      toast.success(result.payload.message || 'Articles deleted successfully');
      dispatch(getKbOverviewStats({ business_id, group_id: selectedGroupId }));
      refreshData();
      setSelected([]);
    } else {
      toast.error(result.payload || 'Failed to delete articles');
    }
  };

  const getTabCount = (key) => {
    if (debouncedSearch) return searchCounts?.[key] || 0;
    const data =
      selectedGroupId &&
      groupStatsGroupId != null &&
      String(groupStatsGroupId) === String(selectedGroupId)
        ? groupStats?.data || {}
        : {};
    switch (key) {
      case 'all': return data.total || 0;
      case 'published': return data.published || 0;
      case 'draft': return data.draft || 0;
      case 'pendingReview': return data.pendingReview || 0;
      case 'archived': return data.archived || 0;
      default: return 0;
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', height: 'calc(100vh - 5.3em)' }}>
      <div className="TicketManagment-Container001" style={{ marginTop: isHeaderInlinePageTitleEnabled ? 0 : '16px' }}>
        {/* Legacy in-page title + group — hidden when header shows them (Application V2) */}
        {!isHeaderInlinePageTitleEnabled && (
          <div className="profile-header-row">
            <p className="Ticket-text">Articles</p>
            <GroupSelectDropdown
              onSelect={setSelectedGroupId}
              value={selectedGroupId}
            />
          </div>
        )}
        <div
          className="Ticket-main-header"
          style={{ marginTop: isHeaderInlinePageTitleEnabled ? 0 : '30px' }}
        >
          <div className="Ticket-text-section">
            {TABS.map((t) => (
              <p
                key={t.key}
                className={`ticket-header-text ${tab === t.key ? "TicketHeaderActive" : ""}`}
                style={{ fontWeight: tab === t.key ? 600 : 400 }}
                onClick={() => { setTab(t.key); setSelected([]); setPage(0); }}
              >
                {t.label} ({getTabCount(t.key)})
              </p>
            ))}
          </div>
        </div>
      </div>

      <ArticlesToolbar
        searchQuery={searchQuery}
        setSearchQuery={(val) => { setSearchQuery(val); setPage(0); }}
        visibility={visibility}
        setVisibility={(val) => { setVisibility(val); setPage(0); }}
        onCreate={() => { navigate('/kb/article/new', { state: { groupId: selectedGroupId } }) }}
        kbRole={kbRole}

      />


      <ArticlesBulkBar
        count={selected.length}
        onBulkChange={handleBulkChange}
        onBulkDelete={handleBulkDelete}
        groupId={selectedGroupId}
      />

      <Box className="Ticket-container-render" sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {isLoading ? (
          <ArticlesSkeleton />
        ) : (
          <ArticlesTable
            rows={articles || []}
            selected={selected}
            setSelected={setSelected}
            onAction={handleRowAction}
            page={page}
            rowsPerPage={rowsPerPage}
            onChangePage={(p) => setPage(p)}
            totalCount={totalCount}
          />
        )}
      </Box>
    </Box>
  );
};

export default Articles;
